import RefreshIcon from '@mui/icons-material/Refresh';
import { Button, Grid, IconButton, MenuItem, Pagination, Select, TextField, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import strings from "../../Globalization/index";
import util from '../../util';
import { useGridBase } from './GridBaseContext';

const MINPAGE = 1;
const GridBasePagination = ({ onRefresh, showGridRefreshBtn, disablePagination, pageSizeOptions = [5, 15, 25, 50] }) => {
	const { patch, getState } = useGridBase();
	const gridBaseState = useMemo(() => getState(), [getState]);
	const [inputValue, setInputValue] = useState("");
	const [pageValue, setPageValue] = useState('');

	const {
		data: { rowCount },
		isLoading,
		paginationModel: { pageSize }
	} = gridBaseState;
	const pageCount = Math.ceil(rowCount / pageSize);
	let { paginationModel: { page } } = gridBaseState;
	const startRow = rowCount === 0 || page > pageCount ? 0 : page * pageSize + 1;
	const endRow = Math.min((page + 1) * pageSize, rowCount);
	page = page > pageCount? pageCount : page;

	const handleOnChange = e => {
		const value = e.target.value.replace(/-/g, '');
		setPageValue(value);
		if (value < MINPAGE) {
			setInputValue(String(MINPAGE));
		} else if (value > pageCount) {
			setInputValue(Number(pageCount));
		} else {
			setInputValue(value);
		}
	};
	const handleOnPaginationChange = (event, value) => {
		const page = value - 1;
		patch((prev) => ({ paginationModel: { ...prev.paginationModel, page } }));
	};
	const handleOnPageSizeChange = (event) => {
		const pageSize = Number(event.target.value);
		patch((prev) => ({ pageSize, paginationModel: { ...prev.paginationModel, pageSize } }));
	};
	const handleOnPageChange = useCallback(() => {
		if(!inputValue) {
			util.showError(strings.EnterPageNumber, strings.Info, 'info');
			return;
		}
		const pageValue = parseInt(inputValue, 10);

		if (pageValue < MINPAGE || pageValue > pageCount) {
			Swal.fire({
				titleText: strings.Info,
				text: `${strings.PageNotFound}: ${pageValue}`,
				icon: 'info',
				confirmButtonText: strings.Ok,
				allowOutsideClick: false
			});
			return;
		}
		patch((prev) => ({ paginationModel: { ...prev.paginationModel, page: pageValue - 1 } }));
		setPageValue('');
	}, [inputValue, pageCount]);


	return (
		!disablePagination && (
				<Grid container direction={{ xs: 'column', lg: 'row' }} sx={{ flex: 1, padding: 1, justifyContent: {xs: "center", lg: "flex-start"} }} size={12} alignItems="center" >
					<Grid						
						container
						size={{xs: 12, lg: "grow"}}
						direction={{ xs: 'column', lg: 'row' }}
						display={'flex'}
						gap={2}
						justifyContent="flex-start"
					>
						<Grid size={{xs: 12, lg: 'auto'}} sx={{ display: 'flex', justifyContent: {xs: "center", lg: "flex-start"} }} alignItems="center">
							<Pagination
								count={pageCount}
								page={page + 1}
								siblingCount={0}
								onChange={handleOnPaginationChange}
							/>
						{pageCount > 0 && <Typography component="span" variant="body2">
							{strings.Page || 'Page'} {page + 1} of {pageCount}
						</Typography>}
						</Grid>
						<Grid size={{xs: 12, lg: 'auto'}} sx={{display: 'flex', alignItems: 'center', justifyContent: {xs: "center", lg: "flex-start",}}} >
							<Select
								value={pageSize}
								displayEmpty
								variant="standard"
								disableUnderline={true}
								onChange={handleOnPageSizeChange}
							>
								{pageSizeOptions.map((pageSizeOption, index) => (
									<MenuItem key={`page-size-option-${pageSizeOption}`} value={pageSizeOption}>
										{pageSizeOption}
									</MenuItem>
								))}
							</Select>
							{strings.RecordsPerPage}
							{showGridRefreshBtn && (
								<IconButton color="primary" size="small" onClick={onRefresh} disabled={isLoading}>
									<RefreshIcon />
								</IconButton>
							)}
						</Grid>
					</Grid>
					<Grid
						container
						size="auto"
						direction={{ xs: 'column', lg: 'row' }}
						display={'flex'}
						gap={2}
						alignItems="center"
					>
						<Grid container size="auto" sx={{ flex: 1, gap: 0.5 }} justifyContent="flex-end">
								<Grid size="auto" sx={{ display: 'flex' }} alignItems="center">
									<TextField
										onChange={handleOnChange}
										fontSize="small"
										size="small"
										variant="outlined"
										type="number"
										sx={{
											maxWidth: '96px',
										}}
										slotProps={{
											inputProps: {
												min: 1,
												max: pageCount,
											},
										}}
										value={pageValue}
									/>
								</Grid>
								<Grid size="auto" sx={{ display: 'flex' }} alignItems="center"></Grid>
								<Button
									size="small"
									color="primary"
									variant="contained"
									fontSize="small"
									sx={{
										backgroundColor: '#1C84C6',
										color: '#fff',
										border: 'none',
										'&:hover': {
											background: '#1C84C6'
										}
									}}
									onClick={handleOnPageChange}
								>
									{strings.GoToPage ?? 'Go To Page'}
								</Button>
							</Grid>
						<Grid size={'auto'} sx={{ display: 'flex'}}>
							<Typography component="span" variant="body2">
								{strings.Displaying || 'Displaying'} {startRow} - {endRow} of {rowCount}{' '}
								{strings.Records || 'Records'}
							</Typography>
						</Grid>
					</Grid>
				</Grid>
			)
	);
};

export default GridBasePagination;